import React, {useState, useEffect} from "react";
import {Helper} from "../../../tools/Helper";
import {api_Routes} from "../../../tools/api_Routes";
import {useNavigate} from "react-router-dom";
import {
    errorToaster,
    successToaster,
} from "../../../helper/toasterConfiguration";
import {Styles} from "./styles";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import PaymentDoc from "./payment-doc";

const PanelAddBalanceRequest = ({
                                    panels,
                                    isPanelsLoading,
                                    users,
                                    isUsersLoading,
                                }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [payment_note, setPayment_note] = useState("");
    const [amount, setAmount] = useState();
    const itemsPerPage = 25;

    // Add state for selected user and filtered panels
    const [selectedUser, setSelectedUser] = useState(null);
    const [userFilteredPanels, setUserFilteredPanels] = useState([]);

    // Panels State
    const [panelsPage, setPanelsPage] = useState(1);
    const [panelSearchQuery, setPanelSearchQuery] = useState("");

    // Filter panels based on selected user's ID
    useEffect(() => {
        if (selectedUser && panels.length > 0) {
            // Filter panels that have user_id equal to the selected user's ID
            const filtered = panels.filter(panel => panel.user_id === selectedUser.id);
            setUserFilteredPanels(filtered);

            // Reset panel selection when user changes
            setData(prev => ({...prev, panel_id: ""}));
        } else {
            // If no user is selected, show all panels
            setUserFilteredPanels(panels);

            // Reset panel selection when user is cleared
            setData(prev => ({...prev, panel_id: ""}));
        }
    }, [selectedUser, panels]);

    // Use userFilteredPanels for display
    const paginatedPanels = userFilteredPanels.slice(0, panelsPage * itemsPerPage);
    const filteredPanels = userFilteredPanels.filter((panel) =>
        panel.name.toLowerCase().includes(panelSearchQuery.toLowerCase())
    );
    const displayedPanels = panelSearchQuery ? filteredPanels : paginatedPanels;

    // Users State
    const [usersPage, setUsersPage] = useState(1);
    const [userSearchQuery, setUserSearchQuery] = useState("");
    const paginatedUsers = users.slice(0, usersPage * itemsPerPage);
    const filteredUsers = users.filter((user) =>
        user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
    );
    const displayedUsers = userSearchQuery ? filteredUsers : paginatedUsers;


    const [data, setData] = useState({
        panel_id: "",
        amount: "",
        payment_method: "",
        user_id: "",
        payment_document: "",
        payment_note: "",
    });

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setData((prev) => ({...prev, [name]: value}));
        setAmount(null);
    };

    const handlePanelChange = (event, value) => {
        setData((prev) => ({...prev, panel_id: value?.id || ""}));
    };

    const handleUserChange = (event, value) => {
        setSelectedUser(value);
        setData((prev) => ({
            ...prev,
            user_id: value?.id || "",
            panel_id: "" // Reset panel selection when user changes
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const requiredFields = [
            "amount",
            "payment_method",
            "user_id",
        ];

        const updatedFormData = {
            ...data,
        };

        const missingFields = requiredFields.filter(
            (field) => !updatedFormData[field]
        );

        if (missingFields.length > 0) {
            errorToaster(
                `Please fill in all required fields: ${missingFields.join(", ")}`
            );
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const form_data = new FormData();
        form_data.append("payment_document", data.payment_document);
        updatedFormData.payment_note = payment_note;
        // Append other form data fields
        Object.keys(updatedFormData).forEach((key) => {
            const value = updatedFormData[key];
            if (value && key !== "payment_document") {
                form_data.append(key, value);
            }
        });

        const result = await Helper.Post({
            url: api_Routes.AddBalanceRequests.Add,
            data: form_data,
            hasToken: true,
        });

        const {response, message} = result || {};

        if (response) {
            successToaster(message);
            navigate("/AddBalanceRequests");
        }
        setIsLoading(false);
    };

    return (
        <Styles>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-6">
                <div className="flex flex-col justify-between items-center gap-6 md:flex-row w-full">
                    {/* Panels Autocomplete */}
                    <div className="w-full">
                        <Autocomplete
                            disablePortal
                            loading={isPanelsLoading}
                            options={displayedPanels}
                            getOptionLabel={(option) => option.name || ""}
                            onChange={handlePanelChange}
                            onInputChange={(event, value) => setPanelSearchQuery(value)}
                            value={displayedPanels.find(panel => panel.id === data.panel_id) || null}
                            disabled={!data.user_id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Panel"
                                    variant="outlined"
                                    helperText={!data.user_id ? "Please select a user first" : ""}
                                />
                            )}
                            ListboxProps={{
                                onScroll: (event) => {
                                    const listboxNode = event.currentTarget;
                                    if (
                                        listboxNode.scrollTop + listboxNode.clientHeight >=
                                        listboxNode.scrollHeight - 3
                                    ) {
                                        setPanelsPage((prevPage) => prevPage + 1);
                                    }
                                },
                            }}
                        />
                    </div>

                    {/* Users Autocomplete */}
                    <div className="w-full">
                        <Autocomplete
                            disablePortal
                            loading={isUsersLoading}
                            options={displayedUsers}
                            getOptionLabel={(option) => option.name || ""}
                            onChange={handleUserChange}
                            onInputChange={(event, value) => setUserSearchQuery(value)}
                            renderInput={(params) => (
                                <TextField {...params} label="User" variant="outlined"/>
                            )}
                            ListboxProps={{
                                onScroll: (event) => {
                                    const listboxNode = event.currentTarget;
                                    if (
                                        listboxNode.scrollTop + listboxNode.clientHeight >=
                                        listboxNode.scrollHeight - 3
                                    ) {
                                        setUsersPage((prevPage) => prevPage + 1);
                                    }
                                },
                            }}
                        />
                    </div>
                </div>
                <div className="flex flex-col justify-between items-center gap-6 md:flex-row">

                </div>
                <div className="flex flex-col justify-between items-center gap-x-6 md:flex-row">
                    <TextField
                        fullWidth
                        label="Amount"
                        name="amount"
                        type={'number'}
                        required
                        autoComplete="off"
                        value={data?.amount || amount || ""}
                        onChange={handleInputChange}
                        variant="outlined"
                        InputLabelProps={{
                            shrink: Boolean(data?.amount || amount),
                        }}
                    />
                    <FormControl className="w-full">
                        <InputLabel id="payment-method-select-label">
                            Payment method *
                        </InputLabel>
                        <Select
                            labelId="payment-method-select-label"
                            id="payment-method-select"
                            value={data.payment_method || null}
                            required
                            onChange={(e) => {
                                setData((prev) => ({
                                    ...prev,
                                    ["payment_method"]: e.target.value,
                                }));
                            }}
                            label="Payment method"
                            sx={{textAlign: "start"}}
                        >
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="bonus">Bonus</MenuItem>
                            <MenuItem value="cryptomus">Cryptomus</MenuItem>
                            <MenuItem value="payeer">Payeer</MenuItem>
                            <MenuItem value="none">None</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <PaymentDoc
                    data={data}
                    setData={setData}
                    payment_note={payment_note}
                    setPayment_note={setPayment_note}
                />

                <div className="submit-btn">
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress size={22} sx={{color: "white"}}/>
                        ) : (
                            "Submit"
                        )}
                    </Button>
                </div>
            </form>
        </Styles>
    );
};

export default PanelAddBalanceRequest;